import { BookingDayService } from "@/modules/bookings.days";
import { ProductService } from "@/modules/products";
import SUPMENU from "@/config/menus/suppliersMenu";
import format from "@/services/format";
import money from "@/services/money";

export default {
  // Compiling Blobs
  compileSupplierBreakdown,
  compileProductPrices,
};

/****************
 * COMPILING BLOBS
 * ************/
function compileSupplierBreakdown(booking_suppliers, compiledProductsRef) {
  /***
   * Calculates total of commisions
   * PARAMS
   *  booking_suppliers       @{Object} bsid => booking_suppliers
   *  compiledProductsRef     @{Object}
   * RETURN
   *  @Object => Grouped supplier (by type) with compiled products
   * ********/

  let blob = {};
  let supid;

  SUPMENU.FULL.forEach((item) => {
    if (item.module === "ALL") return;
    blob[item.module] = {};
  });

  // Extract supplier info (merge seperate booking suppleirs into one)
  // Need locked prices only
  // If no locked prices, not confirmed
  Object.values(booking_suppliers)
    .filter((bs) => {
      // Filter out tour guides that are tour leaders
      if (bs?.type_as !== "TOUR_GUIDE") return true;
      const firstProduct = Object.values(bs.products ?? {})[0];
      return firstProduct?.product_type !== "TOUR_LEADER";
    })
    .forEach((supplier) => {
      if (supplier.nest_type === "ALT") return;
      // bs meta stuff, for confirmed room total and product count
      let typeAs = supplier.type_as;

      supid = supplier.supplier_id;
      if (!blob[typeAs][supid]) {
        blob[typeAs][supid] = {
          id: supplier.id,
          supplier_id: supid,
          name: supplier.name,
          supplier_env: supplier.supplier_env,
          type_as: supplier.type_as,
          status: supplier.status,
          meta: supplier.meta,
          supplier_meta: supplier.supplier_meta,
          products: [],
        };
      }

      if (supplier.status != -1) {
        blob[typeAs][supid].products = [
          ...blob[typeAs][supid].products,
          ...Object.values(supplier.products || {}).map((product) => {
            return compiledProductsRef[`${supplier.id}_${product.id}`];
          }),
          ...[
            compiledProductsRef[`${supplier.id}_PORTERAGE`],
            compiledProductsRef[`${supplier.id}_PARKING_VEHICLE`],
            compiledProductsRef[`${supplier.id}_PARKING_BUS`],
          ].filter((i) => i && i.compiledTotal),
        ].filter((i) => i); // Strip nulls
      }
    });

  // Shift Hotel Meals to be listed + grouped with Hotel products
  // Do not display under restaurant
  // if (blob.RESTAURANT && blob.HOTEL) {
  //   Object.values(blob.RESTAURANT).forEach((supplier) => {
  //     supid = supplier.supplier_id;
  //     if (supid in blob.HOTEL) {
  //       blob.HOTEL[supid].products = [...blob.HOTEL[supid].products, ...supplier.products];
  //       delete blob.RESTAURANT[supid];
  //     }
  //   });
  // }

  return blob;
}

function compileProductPrices({ pax, booking_suppliers, booking_days, roomBreakdown, storeGetter, errorPopup }) {
  /***
   * Calculates total of commisions
   * PARAMS
   *  pax                 @{Number} The default PAX of main booking
   *  booking_suppliers   @{Object} bsid => booking_suppliers
   *  booking_days        @{Array} list of booking days
   *  roomBreakdown       @{Object} Compiled/Breakdown of rooming list
   *  storeGetter         @{Store} Vue store
   *  errorPopup          @{Function} Callback function to assist with error catching
   * RETURN
   *  @Object => Grouped supplier (by type) with compiled products
   * ********/
  let productRef = {};
  let cusname, count, roomCountByDate;

  // TODO: Temporarily remove the duplicated supplier removal code. wll delete the code below if everything works fine.
  // // remove duplicate supplier, remap booking_days
  // const removedDuplicateBookingSuppliers = {};
  // const remapedId = {};
  // Object.values(booking_suppliers).forEach((supplier) =>
  //   supplier.supplier_id in removedDuplicateBookingSuppliers
  //     ? (remapedId[supplier.id] = supplier.supplier_id)
  //     : (removedDuplicateBookingSuppliers[supplier.supplier_id] = supplier)
  // );
  // Object.entries(remapedId).forEach(([key, value]) => (remapedId[key] = removedDuplicateBookingSuppliers[value].id));

  // function remapBookingId(day, key) {
  //   return day[key].map((id) => (id in remapedId ? remapedId[id] : id));
  // }
  // const remapedBookingDays = booking_days.map((day) => {
  //   const excursions = remapBookingId(day, "excursions");
  //   const hotels = remapBookingId(day, "hotels");
  //   const parks = remapBookingId(day, "parks");
  //   const restaurants = remapBookingId(day, "restaurants");
  //   const supplier_rooms = remapBookingId(day, "supplier_rooms");
  //   const tour_guides = remapBookingId(day, "tour_guides");
  //   const transport = remapBookingId(day, "transport");
  //   return { ...day, excursions, hotels, parks, restaurants, supplier_rooms, tour_guides, transport };
  // });
  // const remapedBookingSuppliers = {};
  // Object.entries(removedDuplicateBookingSuppliers).forEach(
  //   ([key, value]) => (remapedBookingSuppliers[value.id] = removedDuplicateBookingSuppliers[key])
  // );

  const remapedBookingSuppliers = _indexBookingSuppliersById(booking_suppliers);
  const remapedBookingDays = booking_days;

  const hasTourLeader = Object.values(remapedBookingSuppliers).some(
    (bs) => bs?.type_as === "TOUR_GUIDE" && Object.values(bs?.products ?? {})[0]?.product_type === "TOUR_LEADER"
  );

  Object.values(remapedBookingSuppliers)
    .filter((bs) => bs.status != -1)
    .forEach((bs) => {
      const bsmeta = bs.meta;
      const lockedPrices = bs.locked_prices || {};

      const whatDays = BookingDayService.whatDays(bs.type_as, bs.id, remapedBookingDays);

      // BUG WORKAROUND -- undeleted booking suppliers?
      if (!whatDays.length) return;

      _transferUnconfirmedPricesToLocked(bs, bs.products || {}, lockedPrices, storeGetter, whatDays[0], errorPopup);

      let metaMod = {
        ...JSON.parse(JSON.stringify(bsmeta)), // Copy to avoid modification
        ...BookingDayService.setConfirmedRoomData({
          item: bs,
          status: 10,
          existingMeta: bsmeta,
          roomBreakdownData: roomBreakdown,
          dayinfo: remapedBookingDays,
        }),
        ...BookingDayService.setConfirmedCount({
          env: storeGetter["AccountStore/environment"],
          item: bs,
          existingMeta: bsmeta,
          bookingSuppliers: remapedBookingSuppliers,
          days: remapedBookingDays,
          // dayinfo,
          overwrite: false,
        }),
      };

      Object.values(bs.products || {}).forEach((product) => {
        cusname = metaMod.custom_names && metaMod.custom_names[product.id];
        count = (metaMod.confirmed_count_total && metaMod.confirmed_count_total[product.id]) || {};
        roomCountByDate = metaMod.confirmed_roomcount;

        // HACK / BUGN WORK AROUND
        if (roomCountByDate && roomCountByDate.using) {
          let fixed = {};
          whatDays.forEach((date) => {
            fixed[date] = roomCountByDate;
          });
          roomCountByDate = fixed;
        }

        // Locked/custom restaurant (or excursion) quantity freezes the client count
        // (e.g. 30), but booking UI still shows live guide/driver meals as "+2".
        // Refresh those extras from the itinerary so reporting matches "30 + 2" → 32.
        if (["RESTAURANT", "EXCURSION"].includes(bs.type_as)) {
          count = _refreshSupplierMealCountsOnConfirmedCount({
            countBreakdown: count,
            bookingSupplier: bs,
            productId: product.id,
            bookingSuppliers: remapedBookingSuppliers,
            days: remapedBookingDays,
            pax,
          });
        }

        // Custom Price
        const customPrices = bs.meta?.custom_prices || {};
        const customPrice = customPrices ? customPrices[product.id] : null;
        const hasCustomPrice = customPrice != null;

        // Custom Price V2
        const customPricesV2 = bs.meta?.custom_prices_v2 || {};
        const customPriceV2 = customPricesV2 ? customPricesV2[product.id] : null;
        const hasCustomPriceV2 = customPriceV2 != null;

        productRef[`${bs.id}_${product.id}`] = {
          id: product.id,
          name: cusname || product.name,
          supplier_id: bs.supplier_id,
          supplier_env: bs.supplier_env,
          type_as: bs.type_as,
          product_type: product.product_type,
          tax_inclusive: product.tax_inclusive,
          product_meta: product.meta,
          seasonal: product.seasonal,
          hasCustomPrice: hasCustomPrice,
          hasCustomPriceV2: hasCustomPriceV2,
          ..._extractProductCompiledTotal({
            dateList: whatDays,
            productId: product.id,
            lockedPrices: lockedPrices,
            typeAs: bs.type_as,
            foc: _getFOC(bs),
            pax,
            count,
            roomCountByDate,
            product,
            supplier: bs,
            hasTourLeader,
            storeGetter,
          }),
        };

        // Fill specialized products
        if (bs.type_as === "HOTEL") {
          productRef[`${bs.id}_PORTERAGE`] = {
            id: "PORTERAGE",
            name: "Porterage",
            supplier_id: bs.supplier_id,
            type_as: bs.type_as,
            product_type: "HARDCODE",
            tax_inclusive: product.tax_inclusive, // Via assuming same as hotel room
            product_meta: product.meta,
            // Assuming pax + max supplier room for hotel is roomcount
            ..._extractPorterage(metaMod, bs.meta.supplier_meta, roomCountByDate, pax),
          };

          productRef[`${bs.id}_PARKING_VEHICLE`] = {
            id: "PARKING_VEHICLE",
            name: "Parking (Vehicles)",
            supplier_id: bs.supplier_id,
            type_as: bs.type_as,
            product_type: "HARDCODE",
            tax_inclusive: product.tax_inclusive, // Via assuming same as hotel room
            product_meta: product.meta,
            ..._extractVehicleParking(metaMod, bs.supplier_meta, whatDays),
          };

          productRef[`${bs.id}_PARKING_BUS`] = {
            id: "PARKING_BUS",
            name: "Parking (Buses)",
            supplier_id: bs.supplier_id,
            type_as: bs.type_as,
            product_type: "HARDCODE",
            tax_inclusive: product.tax_inclusive, // Via assuming same as hotel room
            product_meta: product.meta,
            ..._extractBusParking(metaMod, bs.supplier_meta, whatDays),
          };
        }
      });
    });

  return productRef;
}

function _indexBookingSuppliersById(bookingSuppliers) {
  // Report compile historically received an array from Object.values(...).
  // Meal extras look up guides/drivers by booking_supplier id, so this must be an id map.
  if (!bookingSuppliers) return {};
  if (!Array.isArray(bookingSuppliers)) return bookingSuppliers;

  return bookingSuppliers.reduce((obj, bs) => {
    if (bs && bs.id) obj[bs.id] = bs;
    return obj;
  }, {});
}

function _toDateKey(date) {
  // confirmed_count_total is keyed as YYYY-MM-DD; report days may be ISO timestamps.
  if (!date) return date;
  if (typeof date === "string") return date.slice(0, 10);
  if (date instanceof Date && !Number.isNaN(date.getTime())) return date.toISOString().slice(0, 10);
  return String(date).slice(0, 10);
}

function _lookupByDate(mapping, date) {
  if (!mapping) return null;
  if (mapping[date] != null) return mapping[date];
  const key = _toDateKey(date);
  if (mapping[key] != null) return mapping[key];
  // Fall back to matching any key with the same YYYY-MM-DD prefix
  const foundKey = Object.keys(mapping).find((k) => _toDateKey(k) === key);
  return foundKey != null ? mapping[foundKey] : null;
}

function _transferUnconfirmedPricesToLocked(supplier, products, output, storeGetter, useDate, errorPopup) {
  // Modifier function
  const compile = ProductService.lockPrices(products, supplier, storeGetter, useDate, errorPopup);

  Object.values(products).forEach((product) => {
    let use = __extractNestedFilter(compile[product.id]);

    if (output[product.id]) {
      if (!output[product.id].totalPrice) output[product.id].totalPrice = use.totalPrice || {};
      if (!output[product.id].originalPrice) output[product.id].originalPrice = use.originalPrice || {};
      return;
    }
    output[product.id] = use;
    output[product.id].unconfirmed = true;
  });
}

function _refreshSupplierMealCountsOnConfirmedCount({
  countBreakdown,
  bookingSupplier,
  productId,
  bookingSuppliers,
  days,
  pax,
}) {
  const bsmeta = bookingSupplier.meta || {};
  const daysOn = BookingDayService.whatDays(bookingSupplier.type_as, bookingSupplier.id, days, true);

  const tour_guides = {};
  const transport = {};
  const tour_leaders = {};

  daysOn.forEach((day) => {
    const mealCount = BookingDayService.getIncludedTourGuideTransportMealCount(
      day,
      bookingSuppliers,
      bsmeta.exclude_suppliers && bsmeta.exclude_suppliers[day.date],
      { split: true }
    );

    tour_guides[_toDateKey(day.date)] = mealCount.tour_guides;
    transport[_toDateKey(day.date)] = mealCount.transport;
    tour_leaders[_toDateKey(day.date)] = mealCount.tour_leaders;
  });

  // Prefer explicitly locked product quantity when set (matches booking "30 + 2")
  let lockedCount =
    bsmeta.count_lock && bsmeta.count_lock[productId] && bsmeta.count && bsmeta.count[productId] != null
      ? Number(bsmeta.count[productId])
      : countBreakdown && countBreakdown.count != null
        ? Number(countBreakdown.count)
        : bsmeta.count && bsmeta.count[productId] != null
          ? Number(bsmeta.count[productId])
          : undefined;

  // Quote→booking used to seed passenger products with count=1 while EST pax was still 0.
  // That phantom passenger made reports show 3 meals (1 + guide + driver) instead of 2.
  const countLocked = !!(bsmeta.count_lock && bsmeta.count_lock[productId]);
  if (!countLocked && lockedCount === 1 && !Number(pax)) {
    lockedCount = 0;
  }

  return {
    ...countBreakdown,
    ...(lockedCount != null && !Number.isNaN(lockedCount) ? { count: lockedCount } : {}),
    tour_guides,
    transport,
    tour_leaders,
  };
}

function __extractNestedFilter(blob) {
  if (!blob) return {};
  if (blob[0]) return blob[0];
  return blob;
}

function _getFOC(supplier) {
  const foc = supplier.meta?.supplier_meta?.foc;

  if (!foc) return null;
  if (foc.mode === "no") return null;

  return {
    value: money._num(foc.value),
    per: money._num(foc.per),
    method: foc.mode,
  };
}

function _extractProductCompiledTotal({
  productId,
  lockedPrices,
  typeAs,
  pax,
  count,
  foc,
  dateList,
  roomCountByDate,
  product,
  supplier,
  hasTourLeader,
  storeGetter,
}) {
  const dayCount = dateList.length;
  if (!lockedPrices[productId]) return { dayCount };

  let productPrice = lockedPrices[productId];
  let productRate = productPrice.totalPrice || (productPrice[0] || {}).totalPrice;
  let originalRate = productPrice.originalPrice || (productPrice[0] || {}).originalPrice;

  if (!productRate || !originalRate) return { dayCount };

  let temp = {},
    originalTemp = {};
  if (typeAs === "HOTEL") {
    // Hotels need to account for room
    temp = _getRoomTotal(productRate, roomCountByDate, pax, foc, product, supplier, dateList, storeGetter);
    originalTemp = _getRoomTotal(originalRate, roomCountByDate, pax, foc, product, supplier, dateList, storeGetter);
  } else {
    temp = _getGeneralSupplierTotal(productRate, pax, count, dateList, foc, hasTourLeader);
    originalTemp = _getGeneralSupplierTotal(originalRate, pax, count, dateList, foc, hasTourLeader);
  }

  return {
    ...temp,
    original: originalTemp,
    dayCount,
  };
}

function _getRoomTotal(productRate, roomCountByDate, pax, foc, product, supplier, dateList, storeGetter) {
  // PARAMS
  //	productPrice	@{Object}	Product's rate information
  //	roomCount		@{Object}	The date => booking room breakdown (including supplier rooms)
  //  pax             @{Number}   Total PAX
  //	foc 			@{Object}	FOC
  // RETURN @{Number} 	Total number of rooms

  const rateType = productRate.type;
  // Always strip first: locked raw_value may be a comma-formatted string ("1,775.21").
  // money._num("1,775.21") is NaN, and `perRoomPrice - howManyFOC * NaN` blanks parent totals.
  const value = money._num(money.stripFormatting(productRate.raw_value ?? productRate.value));

  // {"tax":"10","cur":"USD","value":"115.00","raw_value":"115.00","type":"PROOM"}
  // {"type":"PROOM","value":"$100.00","cur":"USD"}

  // {"tax":"10","cur":"USD","value":"1,105.00","raw_value":"1,105.00","type":"PROOM"}
  // {"type":"PROOM","value":"$1,000.00","cur":"USD"}

  let breakCount = 0,
    perTotal;

  let totalCountOfItems = 0;
  let subpricing = {},
    displayFront = "",
    displayNames = "";
  Object.values(roomCountByDate).forEach((roomCount) => {
    if (rateType === "PROOM" || !rateType) {
      // Price per room, so get total rooms
      breakCount = roomCount.room_total || 0;
      perTotal = `${breakCount} ${breakCount === 1 ? "Room" : "Rooms"}`;
      // console.log(`PROOM: ${perTotal}`)
    } else if (rateType === "PP") {
      // Price per persion, get total count
      breakCount = roomCount.pax_total || pax || 0;
      perTotal = `${breakCount} ${breakCount === 1 ? "Person" : "People"}`;
    } else if (rateType === "PG") {
      breakCount = 1;
      perTotal = `${breakCount} ${breakCount === 1 ? "Group" : "Groups"}`;
    } else {
      // No rate type, assume total rooms
      breakCount = roomCount.room_total || roomCount.pax_total || pax || 0;
      perTotal = `${breakCount} Each`;
    }

    totalCountOfItems += breakCount;

    // Add Suppliers to Count
    // Suppliers are 1 room = 1 person, so same regardless of rate type
    // Count refers to number of nights a specific supplier is
    roomCount.extraFilter.forEach((item) => {
      if (subpricing[item.id]) {
        subpricing[item.id].count += 1;
        return;
      }

      if (rateType == "PG") {
        return;
      }

      if (item.type_as === "TOUR_GUIDE") {
        displayFront = item.is_tour_leader ? "Tour Leader" : "Tour Guide";
      } else {
        displayFront = "Bus Driver";
      }
      displayNames = [item.first_name, item.last_name].join(" ");
      subpricing[item.id] = {
        type_as: item.type_as,
        displayName: `${displayFront} ${displayNames ? "(" + displayNames + ")" : ""}`,
        count: 1,
        perTotal: `1 ${rateType === "PROOM" ? "Room" : "Person"}`,
        compiledTotal: money._num(value) * 1,
        productRate: ProductService.formatPrice({
          ...productRate,
          ...{ value },
        }),
      };
    });
  });

  // FOC Adjustment
  let howManyFOC = 0;
  let subPriceTotal = Object.values(subpricing).reduce((total, s) => total + s.count, 0);

  if (foc && Number(foc.per) > 0) {
    let focTop = foc.value;
    let focBase = foc.per;

    if (foc.method === "PerNight") {
      Object.values(roomCountByDate).forEach((dailyCount) => {
        // Include supplier rooms (guide/driver) so FOC matches buy-N-get-next-free with extras
        const supplierRooms = dailyCount.extraTotal ?? (dailyCount.extraFilter || []).length;
        const roomsWithSuppliers = (dailyCount.room_total || 0) + supplierRooms;
        const focForThisNight =
          Math.floor((roomsWithSuppliers - Math.floor(roomsWithSuppliers / focBase)) / focBase) * focTop;
        howManyFOC += focForThisNight;
      });
    } else {
      const totalWithSuppliers = totalCountOfItems + subPriceTotal;
      // This is your original formula
      howManyFOC = Math.floor((totalWithSuppliers - Math.floor(totalWithSuppliers / focBase)) / focBase) * focTop;
      // console.log(`focTop: ${focTop}, focBase: ${focBase}, totalWithSuppliers: ${totalWithSuppliers}`)
      // console.log(`((${totalWithSuppliers} - ${focTop}) / ${focBase}) * ${focTop}`)
      // console.log(`howManyFOC: ${howManyFOC}`)
    }
    if (howManyFOC > 0) {
      totalCountOfItems -= howManyFOC;
      perTotal += ` (${focTop}/${focBase} FOC)`;
    }
  }

  let perRoomPrice = 0,
    perRoomPriceNoOccupancy = 0,
    perRoom = [];

  // Calculate per room type price when the product has per room data
  if (rateType === "PROOM") {
    // Track whether the original room types 'SINGLE_DK' or 'DOUBLE_QK' exist before merging them into 'SINGLE_DOUBLE'.
    let isSingleRoom = false;
    let isDoubleRoom = false;

    // Extract room from roomCountByDate.room_breakdown object
    const roomByDates = Object.values(roomCountByDate);
    const dayCount = roomByDates.length;
    const rooms = roomByDates.reduce((previous, current) => {
      Object.entries(current.room_breakdown).forEach(([type, total]) => {
        return type in previous ? (previous[type] += parseInt(total, 10)) : (previous[type] = parseInt(total, 10));
      });
      return previous;
    }, {});

    const desiredOrder = ["DOUBLE_QK", "SINGLE_DK", "DOUBLE_DOUBLE", "TRIPLE", "QUAD"];
    const sortedRooms = {};
    desiredOrder.forEach((key) => {
      if (key in rooms) {
        sortedRooms[key] = rooms[key];
      }
    });

    // Above is roomByDates.length * roomTotal
    // so it has to divided
    const roomEachDay = Object.fromEntries(
      Object.entries(sortedRooms).map(([type, total]) => [type, total / dayCount])
    );

    Object.entries(roomEachDay).forEach(([type, total]) => {
      const calculatedPerRoom = _calculatePerRoom({
        defaultPrice: money._num(value),
        product,
        supplier,
        roomType: type,
        roomTotal: total,
        productRate,
        howManyFOC,
        foc,
        // subpricing,
        dayCount,
        dateList,
        storeGetter,
      });
      perRoomPrice += calculatedPerRoom.compiledTotal;
      perRoomPriceNoOccupancy += calculatedPerRoom.compiledTotalNoOccupancy;
      perRoom.push(calculatedPerRoom);

      // change subroom price
      if (["DOUBLE_QK"].includes(type)) {
        Object.entries(subpricing).forEach(([key, value]) => {
          const compiledTotal = value.count * money.stripFormatting(calculatedPerRoom.productRate);
          perRoomPrice += compiledTotal;
          return (subpricing[key] = {
            ...value,
            compiledTotal,
            productRate: calculatedPerRoom.productRate,
            original: calculatedPerRoom.original
              ? {
                  productRate: calculatedPerRoom.original.productRate,
                }
              : null,
          });
        });
      }
    });
  }
  perRoomPrice = perRoomPrice - howManyFOC * (value || 0);
  perRoomPriceNoOccupancy = perRoomPriceNoOccupancy - howManyFOC * (value || 0);

  let compiledTotal;
  let compiledTotalNoOccupancy;
  switch (rateType) {
    case "PROOM":
      compiledTotal = perRoomPrice;
      compiledTotalNoOccupancy = perRoomPriceNoOccupancy;
      break;
    case "PG":
      compiledTotal = money._num(value) * totalCountOfItems;
      compiledTotalNoOccupancy = money._num(value) * totalCountOfItems;
      break;
    default:
      compiledTotal = money._num(value) * (totalCountOfItems + subPriceTotal);
      compiledTotalNoOccupancy = money._num(value) * (totalCountOfItems + subPriceTotal);
      break;
  }

  return {
    productRate: ProductService.formatPrice({
      ...productRate,
      ...{ value },
    }),
    perTotal,
    hasfoc: !!foc,
    howManyFOC,
    totalCountOfItems: totalCountOfItems + subPriceTotal + howManyFOC,
    compiledTotal,
    compiledTotalNoOccupancy,
    subpricing: Object.values(subpricing).length ? Object.values(subpricing) : null,
    perRoom,
  };
}

function _calculatePerRoom({
  defaultPrice,
  product,
  supplier,
  roomType,
  roomTotal,
  productRate,
  howManyFOC,
  foc,
  // subpricing,
  dayCount,
  dateList,
  storeGetter,
}) {
  // Initialize variables for room price and the final calculated total
  let roomPrice;
  let roomPriceWithModifier;
  let roomPriceWithModifierNoOccupancy;
  let compiledTotal = 0;
  let compiledTotalNoOccupancy = 0;
  let isDefaultPrice;

  // Metadata for product and supplier
  const productMeta = product.meta;
  const supplierMeta = supplier.supplier_meta;

  // Get custom prices defined for the supplier (if any)
  const customPrices = supplier.meta?.custom_prices || {};
  const customPrice = customPrices ? customPrices[product.id] : null;
  const hasCustomPrices = customPrice != null && Object.keys(customPrice).length > 0;

  // Get custom prices v2 defined for the supplier (if any)
  const customPricesV2 = supplier.meta?.custom_prices_v2 || {};
  const customPriceV2 = customPricesV2 ? customPricesV2[product.id] : null;
  const hasCustomPricesV2 = customPriceV2 != null && Object.keys(customPriceV2).length > 0;

  const isSeasonal = product.seasonal;
  const defaultRoomType = "DOUBLE_QK";

  if (isSeasonal) {
    // Seasonal Price
    // Get seasonal price ranges based on the first date in the date list
    let seasonalPrices = ProductService.getSeasonalRange(product.rate, {
      from_month: format.dateToObj(dateList[0]).month,
      from_day: format.dateToObj(dateList[0]).day,
    });

    if (hasCustomPrices) {
      // Replace rate with custom price
      seasonalPrices.forEach((price, i) => {
        if (customPrice && customPrice[i]) {
          price.rate = customPrice[i];
        }
      });
    } else if (hasCustomPricesV2) {
      // Replace rate with custom price v2
      seasonalPrices = [];
      customPriceV2.forEach((price) => {
        seasonalPrices.push({
          mstart: 1,
          mend: 12,
          dstart: 1,
          dend: 31,
          roomType: price.roomType,
          rate: price.price,
        });
      });
    }

    // Find the default, single/double, and null seasonal prices
    const defaultSeasonalPrice = seasonalPrices.find((price) => price.roomType === defaultRoomType);
    const nullSeasonalPrice = seasonalPrices.find((price) => price.roomType === null || price.roomType === undefined);

    // Determine the default price to use
    let fallbackPrice = defaultSeasonalPrice || nullSeasonalPrice;
    let matchedPrice;

    // If the room type is one of the specified, select the corresponding seasonal price
    if (["SINGLE_DK", "DOUBLE_QK", "DOUBLE_DOUBLE", "TRIPLE", "QUAD"].includes(roomType)) {
      matchedPrice = seasonalPrices.find((price) => price.roomType == roomType);
    }

    // If no price was found for the roomType, fallback to the default or null price
    let defaultPrice = matchedPrice;
    if (!defaultPrice) {
      defaultPrice = fallbackPrice;
    }

    // Calculate additional costs and apply extras
    roomPrice = defaultPrice?.rate?.value != null ? money.stripFormatting(defaultPrice.rate.value) : 0;
    roomPriceWithModifier = _getRoomPriceWithModifier(
      storeGetter,
      matchedPrice,
      roomType,
      defaultPrice.rate,
      product,
      productRate,
      supplier,
      true
    );
    roomPriceWithModifierNoOccupancy = _getRoomPriceWithModifier(
      storeGetter,
      matchedPrice,
      roomType,
      defaultPrice.rate,
      product,
      productRate,
      supplier,
      false
    );

    isDefaultPrice = matchedPrice == null && roomType !== defaultRoomType;
  } else {
    // Flat Price
    let roomRate;
    let matchedRate;

    const perRoomRates = productMeta.per_room;
    const hasSpecificRoomRate = perRoomRates && perRoomRates.length > 0;

    if (hasSpecificRoomRate) {
      matchedRate = perRoomRates.find((rate) => rate.type.includes(roomType));
      roomRate = matchedRate || product.rate;
    } else {
      roomRate = product.rate;
    }

    if (hasCustomPrices) {
      roomRate = customPrice;
    } else if (hasCustomPricesV2) {
      const emptyPrice = customPriceV2.find((price) => price.roomType === "");
      const defaultPrice = customPriceV2.find((custom) => custom.roomType === defaultRoomType);
      matchedRate = customPriceV2.find((item) => item.roomType === roomType);

      if (matchedRate) {
        roomRate = matchedRate.price;
      } else if (defaultPrice) {
        roomRate = defaultPrice.price;
      } else if (emptyPrice) {
        roomRate = emptyPrice.price;
      }
    }

    // Calculate room price
    roomPrice = roomRate?.value != null ? money.stripFormatting(roomRate.value) : 0;
    roomPriceWithModifier = _getRoomPriceWithModifier(
      storeGetter,
      matchedRate,
      roomType,
      roomRate,
      product,
      productRate,
      supplier,
      true
    );
    roomPriceWithModifierNoOccupancy = _getRoomPriceWithModifier(
      storeGetter,
      matchedRate,
      roomType,
      roomRate,
      product,
      productRate,
      supplier,
      false
    );
    isDefaultPrice = matchedRate == null && roomType !== "DOUBLE_QK";
  }

  // Calculate total number of rooms based on room count and day count
  const roomCount = roomTotal * dayCount;

  // Calculate total price based on room type and number of FOC (free-of-charge) rooms
  let perTotal = `${roomTotal} ${roomTotal === 1 ? "Room" : "Rooms"}`;
  // if (roomType === "SINGLE_DOUBLE" && foc) {
  //   compiledTotal = roomPriceWithModifier * (roomCount - howManyFOC);
  //   compiledTotalNoOccupancy = roomPriceWithModifierNoOccupancy * (roomCount - howManyFOC);
  // } else {
  compiledTotal = roomPriceWithModifier * roomCount;
  compiledTotalNoOccupancy = roomPriceWithModifierNoOccupancy * roomCount;
  // }

  // Determine the display name for the room type
  let roomName;
  switch (roomType) {
    case "SINGLE_DK":
      roomName = "Single";
      break;
    case "DOUBLE_QK":
      roomName = "Double";
      break;
    case "DOUBLE_DOUBLE":
      roomName = "Twin";
      break;
    case "TRIPLE":
      roomName = "Triple";
      break;
    case "QUAD":
      roomName = "Quad";
      break;
  }

  // Return the final calculated data
  return {
    name: roomName,
    type: roomType,
    totalCountOfItems: roomCount,
    perTotal,
    hasfoc: !!foc,
    howManyFOC,
    productRate: ProductService.formatPrice({ ...productRate, value: roomPriceWithModifier }),
    compiledTotal,
    compiledTotalNoOccupancy,
    // subpricing: Object.values(subpricing),
    original:
      roomPrice === defaultPrice
        ? null
        : {
            productRate: ProductService.formatPrice({ ...productRate, value: roomPrice }),
          },
    isDefaultPrice: isDefaultPrice,
  };
}

function _getGeneralSupplierTotal(productRate, pax, countBreakdown, dayList, foc) {
  const rateType = productRate.type;
  const value = money.stripFormatting(productRate.raw_value || productRate.value);
  let breakCount, perTotal;

  // FOC Adjustment
  let totalCountOfItems = 0;
  let howManyFOC = 0;

  if (rateType === "PP") {
    // PER PERSON — treat 0 as a real quantity (guide/driver-only bookings before EST pax)
    breakCount = countBreakdown.count != null ? Number(countBreakdown.count) : Number(pax || 0);
    perTotal = `${breakCount} ${breakCount === 1 ? "Person" : "People"}`;
  } else if (rateType === "PG" || rateType === "PD") {
    // PER GROUP
    breakCount = countBreakdown.count != null ? Number(countBreakdown.count) : 1;
    if (breakCount > 1) perTotal = breakCount;
  } else {
    breakCount = countBreakdown.count != null ? Number(countBreakdown.count) : 1;
    perTotal = `${breakCount}`;
  }

  totalCountOfItems = breakCount * dayList.length;

  // Add Suppliers to Count
  // Suppliers can be tour guide or bus driver
  // Only need count total if
  let tourGuideMapping = {},
    transportMapping = {},
    tourLeaderMapping = {},
    tgnum,
    transnum,
    tlnum;
  dayList.forEach((date) => {
    tgnum = _lookupByDate(countBreakdown.tour_guides, date);
    if (tgnum) {
      if (!tourGuideMapping[tgnum]) tourGuideMapping[tgnum] = [];
      tourGuideMapping[tgnum].push(date);
    }
    transnum = _lookupByDate(countBreakdown.transport, date);
    if (transnum) {
      if (!transportMapping[transnum]) transportMapping[transnum] = [];
      transportMapping[transnum].push(date);
    }
    tlnum = _lookupByDate(countBreakdown.tour_leaders, date);
    if (tlnum) {
      if (!tourLeaderMapping[tlnum]) tourLeaderMapping[tlnum] = [];
      tourLeaderMapping[tlnum].push(date);
    }
  });

  let subPriceTotal = 0,
    subcount;
  let subpricing = [];
  Object.keys(tourGuideMapping).forEach((c) => {
    subcount = tourGuideMapping[c].length;
    subPriceTotal += c * subcount;
    subpricing.push({
      type_as: "MEAL", // For now...
      displayName: "Tour Guide",
      count: subcount,
      perTotal: rateType === "PP" ? `${c} ${Number(c) === 1 ? "Person" : "People"}` : c,
      compiledTotal: money._num(value) * subcount * c,
    });
  });
  Object.keys(transportMapping).forEach((c) => {
    subcount = transportMapping[c].length;
    subPriceTotal += c * subcount;
    subpricing.push({
      type_as: "MEAL", // For now...
      displayName: "Bus Driver",
      count: subcount,
      perTotal: rateType === "PP" ? `${c} ${Number(c) === 1 ? "Person" : "People"}` : c,
      compiledTotal: money._num(value) * subcount * c,
    });
  });
  Object.keys(tourLeaderMapping).forEach((c) => {
    subcount = tourLeaderMapping[c].length;
    subPriceTotal += c * subcount;
    subpricing.push({
      type_as: "MEAL", // For now...
      displayName: "Tour Leader",
      count: subcount,
      perTotal: rateType === "PP" ? `${c} ${Number(c) === 1 ? "Person" : "People"}` : c,
      compiledTotal: money._num(value) * subcount * c,
    });
  });

  // Free of charge adjustment
  if (foc) {
    let focTop = foc.value;
    let focBase = foc.per || 1;
    let totalWithSuppliers = totalCountOfItems + subPriceTotal;

    howManyFOC = Math.floor((totalWithSuppliers - Math.floor(totalWithSuppliers / focBase)) / focBase) * focTop;
    totalCountOfItems -= totalCountOfItems >= howManyFOC ? howManyFOC : 0;

    perTotal += ` (${focTop}/${focBase} FOC)`;
    // console.log(`charge adj : ${perTotal}`)
  }

  return {
    productRate: ProductService.formatPrice(productRate),
    perTotal,
    hasfoc: !!foc,
    howManyFOC,
    totalCountOfItems: totalCountOfItems + subPriceTotal + howManyFOC,
    compiledTotal: money._num(value) * (totalCountOfItems + subPriceTotal),
    subpricing,
  };
}

function _extractPorterage(bsMeta, supplierMeta, roomCountByDate, pax) {
  if (!supplierMeta || !supplierMeta.porterage) return null;
  if (supplierMeta.porterage.mode === "optional" && !bsMeta.porterage) return null;

  const stripped = money.stripFormatting(supplierMeta.porterage.value);
  let countTotal = pax;
  if (!pax) {
    const roomCount = roomCountByDate.using ? roomCountByDate : Object.values(roomCountByDate)[0];
    countTotal = roomCount.pax_total + roomCount.extraTotal;
  }

  return {
    totalCountOfItems: countTotal,
    rate: supplierMeta.porterage.value,
    compiledTotal: money._num(stripped) * countTotal,
  };
}

function _extractVehicleParking(bsMeta, supplierMeta, whatDays) {
  if (!supplierMeta || !supplierMeta.parking || !supplierMeta.parking.per_vehicle) return null;
  if (!bsMeta || !bsMeta.parking || !bsMeta.parking.parking_vehicle_count) return null;

  const stripped = money.stripFormatting(supplierMeta.parking.per_vehicle.value);
  const dayCount = whatDays.length;

  return {
    totalCountOfItems: bsMeta.parking.parking_vehicle_count,
    rate: supplierMeta.parking.per_vehicle.value,
    dayCount,
    compiledTotal: money._num(stripped) * dayCount * money._num(bsMeta.parking.parking_vehicle_count),
  };
}

function _extractBusParking(bsMeta, supplierMeta, whatDays) {
  if (!supplierMeta || !supplierMeta.parking || !supplierMeta.parking.per_bus) return null;
  if (!bsMeta || !bsMeta.parking || !bsMeta.parking.parking_bus_count) return null;

  const stripped = money.stripFormatting(supplierMeta.parking.per_bus.value);
  const dayCount = whatDays.length;

  return {
    totalCountOfItems: bsMeta.parking.parking_bus_count,
    rate: supplierMeta.parking.per_bus.value,
    dayCount,
    compiledTotal: money._num(stripped) * dayCount * money._num(bsMeta.parking.parking_bus_count),
  };
}

function _getTax(product, supplierMeta) {
  // Tax
  const customTax = product.meta?.custom_tax || 0;
  const tax = customTax || supplierMeta?.tax || 0;
  return tax;
}

function _calculatePriceWithExtras(rate, product, bookingSupplierMeta, supplierMeta) {
  if (rate == null) return 0;

  // Extra list
  const extraList = ProductService.getExtraList(product);

  // Occupancy rate for rooms
  const occupancy =
    product.product_type === "ROOM"
      ? (product.meta?.custom_occupancy ?? bookingSupplierMeta?.supplier_meta?.occupancy)
      : 0;

  // Resort fee handling
  const curresort =
    bookingSupplierMeta?.has_custom_resortfee && bookingSupplierMeta?.custom_resortfee_value?.value
      ? bookingSupplierMeta.custom_resortfee_value
      : bookingSupplierMeta?.supplier_meta?.resort_fee?.value
        ? bookingSupplierMeta.supplier_meta.resort_fee
        : null;

  const resortFee = product.product_type === "ROOM" ? curresort || supplierMeta.resort_fee : 0;

  return ProductService.calcTotal({
    rateInfo: rate,
    tax: _getTax(product, supplierMeta),
    taxInclusive: 1,
    extraList: extraList,
    occupancy: occupancy,
    resort_fee: resortFee,
  });
}

function _getRoomPriceWithModifier(
  storeGetter,
  matchedPrice,
  roomType,
  roomRate,
  product,
  productRate,
  supplier,
  includeOccupancy
) {
  const environment = storeGetter["AccountStore/environment"];
  const priceWithExtras = _calculatePriceWithExtras(roomRate, product, supplier.meta, supplier.supplier_meta);
  const taxRate = product.tax_inclusive === 0 ? (productRate?.tax ?? 0) : 0;
  const occupancy =
    includeOccupancy && product.product_type === "ROOM"
      ? (product.meta?.custom_occupancy ?? supplier.meta?.supplier_meta?.occupancy ?? null)
      : null;
  const occupancyValue = occupancy ? money.stripFormatting(occupancy.value) : 0;

  let basePrice = priceWithExtras;
  if (environment !== "USA") {
    basePrice += occupancyValue;
  }

  let total = basePrice + basePrice * (taxRate / 100);
  if (environment === "USA") {
    total += occupancyValue;
  }

  return total;
}

function _calculateAdditionalPrice(storeGetter, roomType) {
  const environment = storeGetter["AccountStore/environment"];
  const priceModifiers = {
    USA: {
      TRIPLE: 20,
      DOUBLE_DOUBLE: 20,
      QUAD: 40,
    },
    CDA: {
      TRIPLE: 20,
      QUAD: 40,
    },
  };
  return priceModifiers[environment]?.[roomType] || 0;
}
